import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { 
  Youtube, 
  Globe, 
  AlertTriangle, 
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Settings,
  Shield
} from 'lucide-react';
import GroupOptions from './GroupOptions';
import DeviceStatusPanel from './DeviceStatusPanel';
import styles from '../CSS/DeviceGroupManager.module.css';

const DeviceGroupManager = ({ selectedDeviceUDIDs = [], onReload, devices = [], deviceNames = {} }) => {
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showOptionsYoutube, setShowOptionsYoutube] = useState(false);
  const [showOptionsSafari, setShowOptionsSafari] = useState(false);
  const [devicesStatus, setDevicesStatus] = useState({});
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const db = getFirestore();
  
  const youtubeButtonRef = useRef(null);
  const safariButtonRef = useRef(null);
  
  useEffect(() => {
    if (selectedDeviceUDIDs.length === 0) {
      setDevicesStatus({});
      return;
    }

    const unsubscribers = selectedDeviceUDIDs.map(udid => 
      onSnapshot(doc(db, 'devices', udid), (snapshot) => {
        const data = snapshot.data() || {};
        
        setDevicesStatus(prevStatus => ({
          ...prevStatus,
          [udid]: {
            ...prevStatus[udid],
            id: udid,
            name: deviceNames[udid] || prevStatus[udid]?.name || udid,
            groupRestrictions: data.groupRestrictions || {},
            previousRestrictions: data.previousRestrictions || {}
          }
        }));
      })
    );

    return () => unsubscribers.forEach(unsub => unsub());

  }, [selectedDeviceUDIDs, db, deviceNames]);

  useEffect(() => {
    if (Object.keys(deviceNames).length === 0 || Object.keys(devicesStatus).length === 0) {
      return;
    }

    setDevicesStatus(prevStatus => {
      const newStatus = { ...prevStatus };
      let hasChanges = false;

      for (const udid in newStatus) {
        if (selectedDeviceUDIDs.includes(udid) && deviceNames[udid]) {
          const currentName = newStatus[udid].name;
          const newName = deviceNames[udid];
          
          if (currentName !== newName) {
            newStatus[udid] = {
              ...newStatus[udid],
              name: newName
            };
            hasChanges = true;
          }
        }
      }

      return hasChanges ? newStatus : prevStatus;
    });
  }, [deviceNames, selectedDeviceUDIDs]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    
    const timer = setTimeout(() => {
      setMessage('');
    }, 8000);
    
    return () => clearTimeout(timer);
  };

  const setEnhancedMessage = (text, type = 'info') => {
    handleMessage(text, type);
  };

  const toggleOptions = (menu) => {
    if (!selectedDeviceUDIDs || selectedDeviceUDIDs.length === 0) {
      handleMessage('Selecciona al menos un dispositivo primero', 'error');
      return;
    }

    if (menu === 'youtube') {
      setShowOptionsYoutube(!showOptionsYoutube);
      if (showOptionsSafari) setShowOptionsSafari(false);
    } else if (menu === 'safari') {
      setShowOptionsSafari(!showOptionsSafari);
      if (showOptionsYoutube) setShowOptionsYoutube(false);
    }
  };

  const closeOptions = (menu) => {
    if (menu === 'youtube') {
      setShowOptionsYoutube(false);
    } else if (menu === 'safari') {
      setShowOptionsSafari(false);
    }
  };

  const getGroupStatus = (groupId) => {
    if (!selectedDeviceUDIDs || selectedDeviceUDIDs.length === 0) return 'neutral';
    
    const blockedDevices = selectedDeviceUDIDs.filter(udid => {
      const device = devicesStatus[udid];
      const groupRestriction = device?.groupRestrictions?.[groupId];
      return groupRestriction?.status === 'blocked';
    }).length;
    
    if (blockedDevices === 0) return 'neutral';
    if (blockedDevices === selectedDeviceUDIDs.length) return 'blocked';
    return 'partial';
  };

  const hasPendingRestrictions = (groupId) => {
    if (!selectedDeviceUDIDs || selectedDeviceUDIDs.length === 0) return false;
    
    return selectedDeviceUDIDs.some(udid => {
      const device = devicesStatus[udid];
      return device?.previousRestrictions?.[groupId];
    });
  };

  const youtubeStatus = getGroupStatus(266);
  const safariStatus = getGroupStatus(133);
  const youtubePending = hasPendingRestrictions(266);
  const safariPending = hasPendingRestrictions(133);

  const totalBlocked = selectedDeviceUDIDs.filter(udid => {
    const device = devicesStatus[udid];
    if (!device) return false;
    const hasYoutubeBlock = device?.groupRestrictions?.[266]?.status === 'blocked';
    const hasSafariBlock = device?.groupRestrictions?.[133]?.status === 'blocked';
    return hasYoutubeBlock || hasSafariBlock;
  }).length;

  const toggleStatusPanel = () => {
    setShowStatusPanel(!showStatusPanel);
  };

  return (
    <div className={styles.deviceGroupManager}>
      <div className={styles.headerCompact}>
        <div className={styles.titleSection}>
          <Settings size={18} className={styles.titleIcon} />
          <h3 className={styles.title}>Gestión de Restricciones</h3>
          
          {selectedDeviceUDIDs && selectedDeviceUDIDs.length > 0 && (
            <div className={styles.deviceCount}>
              <CheckCircle size={16} className={styles.infoIcon} />
              <span>{selectedDeviceUDIDs.length}</span>
              {totalBlocked > 0 && (
                <span className={styles.blockedBadge}>{totalBlocked}</span>
              )}
            </div>
          )}
        </div>
        
        <div className={styles.controlsSection}>
          <div className={styles.controlButtons}>
            <button 
              ref={youtubeButtonRef}
              className={`${styles.controlButton} ${styles.youtubeButton} ${styles[youtubeStatus]} ${youtubePending ? styles.pending : ''} ${showOptionsYoutube ? styles.active : ''}`} 
              onClick={() => toggleOptions('youtube')}
              aria-label="Gestionar restricciones de YouTube"
              disabled={selectedDeviceUDIDs.length === 0}
            >
              <Youtube size={isSmallScreen ? 16 : 18} />
              <span>YouTube</span>
              {youtubePending && <div className={styles.pendingIndicator} />}
            </button>
            
            <button 
              ref={safariButtonRef}
              className={`${styles.controlButton} ${styles.safariButton} ${styles[safariStatus]} ${safariPending ? styles.pending : ''} ${showOptionsSafari ? styles.active : ''}`} 
              onClick={() => toggleOptions('safari')}
              aria-label="Gestionar restricciones de Safari"
              disabled={selectedDeviceUDIDs.length === 0}
            >
              <Globe size={isSmallScreen ? 16 : 18} />
              <span>Safari</span>
              {safariPending && <div className={styles.pendingIndicator} />}
            </button>
            
            {selectedDeviceUDIDs && selectedDeviceUDIDs.length > 0 && (
              <button 
                className={styles.statusToggle}
                onClick={toggleStatusPanel}
                aria-label={showStatusPanel ? "Ocultar detalles" : "Ver detalles"}
              >
                {showStatusPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {message && (
        <div className={`${styles.message} ${styles[messageType]}`}>
          {messageType === 'error' && <AlertTriangle size={16} />}
          {messageType === 'success' && <CheckCircle size={16} />}
          <p>{message}</p>
        </div>
      )}
      
      {showOptionsYoutube && (
        <div className={styles.optionsContainer} style={{ position: 'static' }}>
          <GroupOptions
            groupId={266}
            selectedDeviceUDIDs={selectedDeviceUDIDs || []}
            onReload={onReload}
            setMessage={setEnhancedMessage}
            colorClass="red"
            label="YouTube"
            onClose={() => closeOptions('youtube')}
            anchorEl={youtubeButtonRef.current}
            devices={devices}
          />
        </div>
      )}

      {showOptionsSafari && (
        <div className={styles.optionsContainer} style={{ position: 'static' }}>
          <GroupOptions
            groupId={133}
            selectedDeviceUDIDs={selectedDeviceUDIDs || []}
            onReload={onReload}
            setMessage={setEnhancedMessage}
            colorClass="blue"
            label="Safari"
            onClose={() => closeOptions('safari')}
            anchorEl={safariButtonRef.current}
            devices={devices}
          />
        </div>
      )}
      
      {showStatusPanel && Object.keys(devicesStatus || {}).length > 0 && (
        <div className={styles.statusPanelContainer}>
          <DeviceStatusPanel 
            deviceData={devicesStatus} 
            devices={devices}
            deviceNames={deviceNames}
          />
        </div>
      )}
    </div>
  );
};

export default DeviceGroupManager;